import { useCallback, useEffect, useRef, useState } from 'react'

/*
 * Zusammenarbeit mehrerer Fenster desselben Moduls (Multimonitor) über BroadcastChannel.
 * Der Kanal gilt je Ursprung (also je Modul) und je Browserprofil. Ohne BroadcastChannel (alte Browser) bleibt alles
 * funktionsfähig, nur ohne Abgleich zwischen Fenstern.
 */

const KANAL_PREFIX = 'pilot-hub:fenster:'
const kanaele = new Map<string, BroadcastChannel>()

/** Gemeinsamer Kanal je Name (ein Objekt je Seite). */
export function fensterKanal(name: string): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null
  let k = kanaele.get(name)
  if (!k) { k = new BroadcastChannel(KANAL_PREFIX + name); kanaele.set(name, k) }
  return k
}

/** Eindeutige Kennung dieses Fensters, damit eigene Nachrichten ignoriert werden. */
export const FENSTER_ID = Math.random().toString(36).slice(2, 10)

interface WertNachricht { t: 'wert'; key: string; wert: unknown; von: string }
interface FrageNachricht { t: 'frage'; key: string; von: string }

/**
 * Wert, der in allen Fenstern des Moduls gleich ist (z. B. gewählte Drohne). Eine Änderung in einem Fenster
 * erscheint sofort in den anderen. Ein neu geöffnetes Fenster fragt den aktuellen Wert bei den anderen ab.
 * `setze(wert, false)` ändert nur lokal (ohne Senden).
 */
export function useGeteilterWert<T>(kanal: string, key: string, start: T): [T, (v: T, senden?: boolean) => void] {
  const [wert, setWert] = useState<T>(start)
  const ref = useRef<T>(start)
  ref.current = wert
  const gesetzt = useRef(false) // ist schon bewusst etwas gewählt worden?

  useEffect(() => {
    const k = fensterKanal(kanal)
    if (!k) return
    const on = (e: MessageEvent) => {
      const m = e.data as WertNachricht | FrageNachricht
      if (!m || m.von === FENSTER_ID || m.key !== key) return
      if (m.t === 'wert') { gesetzt.current = true; setWert(m.wert as T) }
      else if (m.t === 'frage' && gesetzt.current) k.postMessage({ t: 'wert', key, wert: ref.current, von: FENSTER_ID } satisfies WertNachricht)
    }
    k.addEventListener('message', on)
    k.postMessage({ t: 'frage', key, von: FENSTER_ID } satisfies FrageNachricht)
    return () => k.removeEventListener('message', on)
  }, [kanal, key])

  const setze = useCallback((v: T, senden = true) => {
    if (senden) gesetzt.current = true
    setWert(v)
    if (senden) fensterKanal(kanal)?.postMessage({ t: 'wert', key, wert: v, von: FENSTER_ID } satisfies WertNachricht)
  }, [kanal, key])

  return [wert, setze]
}

/**
 * Wiederholte Abfrage, die sich mehrere Fenster teilen: Jedes Fenster verteilt seine Daten an die anderen.
 * Ein Fenster fragt nur selbst ab, wenn länger als 1,6 Takte keine Daten von einem anderen Fenster kamen
 * (also in der Regel nur eines; fällt es aus oder ist verdeckt, übernimmt ein anderes nach wenigen Sekunden).
 * Nachrichten der anderen Fenster bleiben unverändert; nur sichtbare Fenster fragen ab.
 */
export function useGeteilteAbfrage<T>(kanal: string, key: string, ms: number, holen: () => Promise<T>, start: T, fehlerText: string) {
  const [daten, setDaten] = useState<T>(start)
  const [fehler, setFehler] = useState<string | null>(null)
  const [geladen, setGeladen] = useState(false)
  const letzterFremder = useRef(0)
  const holenRef = useRef(holen)
  holenRef.current = holen
  const laeuft = useRef(false)

  const abfragen = useCallback(() => {
    if (laeuft.current) return
    laeuft.current = true
    holenRef.current()
      .then(d => {
        setDaten(d); setFehler(null)
        fensterKanal(kanal)?.postMessage({ t: 'daten', key, daten: d, von: FENSTER_ID })
      })
      .catch(() => setFehler(fehlerText))
      .then(() => { laeuft.current = false; setGeladen(true) })
  }, [kanal, key, fehlerText])

  useEffect(() => {
    const k = fensterKanal(kanal)
    const on = (e: MessageEvent) => {
      const m = e.data
      if (!m || m.von === FENSTER_ID || m.key !== key) return
      if (m.t === 'daten') { letzterFremder.current = Date.now(); setDaten(m.daten as T); setFehler(null); setGeladen(true) }
    }
    k?.addEventListener('message', on)
    abfragen()
    const t = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return
      if (Date.now() - letzterFremder.current >= ms * 1.6) abfragen()
    }, ms)
    const retry = () => abfragen()
    window.addEventListener('connection:retry', retry)
    return () => { k?.removeEventListener('message', on); window.clearInterval(t); window.removeEventListener('connection:retry', retry) }
  }, [kanal, key, ms, abfragen])

  return { daten, fehler, geladen, neuLaden: abfragen }
}
